import { Grid, TextField } from "@material-ui/core";
import React from "react";
import { WithIntlProps, injectIntl } from "react-intl";
import ZipCodes from "../../data/zipCodes.json";
import { LocationViewModel } from "../../model/LocationViewModel";
import { GetTranslation } from "../../translations/GetTranslation";
import FormGridCell from "../common/FormGridCell";
import { CantonSelect } from "../common/customAutocomplete/CantonSelect";
import LanguageCheckboxList from "../common/customCheckBox/LanguageCheckboxList";
import EmailTextField from "../common/textField/EmailTextField";
import TranslatedMessage from "../translations/TranslatedMessage";

type LocationFieldsProps = {
  location: LocationViewModel;
  onChangeEmailIsValid?: (isEmailValid: boolean) => void;
  onPropertyValueChanges?: (part: Partial<LocationViewModel>) => void;
} & WithIntlProps<any>;

const LocationFields: React.FunctionComponent<LocationFieldsProps> = (props) => {
  const handleTextChange =
    (key: keyof LocationViewModel) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      handleChange(key, event.target.value);
  const handleValueChange = (key: keyof LocationViewModel) => (value?: string | string[]) => handleChange(key, value);
  const handleZipCodeChange =
    (zipCodeKey: keyof LocationViewModel, cityKey: keyof LocationViewModel) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleChange(zipCodeKey, event.target.value);
      handleChange(cityKey, "");
      ZipCodes.forEach((zipCode) => {
        if (zipCode.zipCode === event.target.value) {
          handleChange(cityKey, zipCode.city);
        }
      });
    };
  const handleChange = (key: keyof LocationViewModel, value?: string | string[]) => {
    let part: Partial<LocationViewModel> = {
      [key]: value,
    };

    if (props.onPropertyValueChanges) {
      props.onPropertyValueChanges(part);
    }
  };
  const { intl } = props;
  return (
    <Grid container={true} spacing={1}>
      <FormGridCell size="full">
        <TextField
          fullWidth={true}
          label={GetTranslation(intl, "location.field.name")}
          value={props.location.name}
          onChange={handleTextChange("name")}
        />
      </FormGridCell>
      <FormGridCell size="fill" />
      <FormGridCell size="full">
        <TextField
          fullWidth={true}
          label={GetTranslation(intl, "location.field.street")}
          value={props.location.street}
          onChange={handleTextChange("street")}
        />
      </FormGridCell>
      <FormGridCell size="fill" />
      <FormGridCell size="small">
        <TextField
          fullWidth={true}
          label={GetTranslation(intl, "location.field.zipCode")}
          value={props.location.zipCode}
          onChange={handleZipCodeChange("zipCode", "city")}
        />
      </FormGridCell>
      <FormGridCell size="medium">
        <TextField
          fullWidth={true}
          label={GetTranslation(intl, "location.field.city")}
          value={props.location.city}
          onChange={handleTextChange("city")}
        />
      </FormGridCell>
      <FormGridCell size="fill" />
      <FormGridCell size="half">
        <CantonSelect value={props.location.canton} onChange={handleValueChange("canton")} />
      </FormGridCell>
      <FormGridCell size="half">
        <LanguageCheckboxList
          label={GetTranslation(intl, "location.field.languageRegions")}
          checkedLanguages={props.location.languageRegions}
          onSelectedLanguagesChanged={handleValueChange("languageRegions")}
        />
      </FormGridCell>
      <FormGridCell size="fill" />
      <FormGridCell size="full">
        <TextField
          fullWidth={true}
          label={GetTranslation(intl, "location.field.contactName")}
          value={props.location.contactName}
          onChange={handleTextChange("contactName")}
        />
      </FormGridCell>
      <FormGridCell size="fill" />
      <FormGridCell size="half">
        <TextField
          fullWidth={true}
          label={GetTranslation(intl, "location.field.contactPhoneNo")}
          value={props.location.contactPhoneNo}
          onChange={handleTextChange("contactPhoneNo")}
        />
      </FormGridCell>
      <FormGridCell size="half">
        <EmailTextField
          label={<TranslatedMessage id="location.field.contactEmailAddress" />}
          value={props.location.contactEmailAddress}
          onChange={handleTextChange("contactEmailAddress")}
          onChangeIsValid={props.onChangeEmailIsValid}
        />
      </FormGridCell>
    </Grid>
  );
};

LocationFields.defaultProps = {
  onChangeEmailIsValid: () => {},
  onPropertyValueChanges: () => {},
} as Partial<LocationFieldsProps>;

export default injectIntl(LocationFields);
